import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

export default function ApproveCommentModal({
  isOpen,
  onClose,
  onSubmit,
  userId,
  numPlate,
  title = "Add a note to complete the process",
  submitLabel = "Submit",
}) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const CAMCOM_GREEN = "#36A149";

  const canSubmit = comment.trim().length > 0;

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setComment("");
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleClose = (force = false) => {
    if (isSubmitting && !force) return;
    setIsAnimating(false);
    setTimeout(() => {
      onClose?.();
    }, 200);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await onSubmit?.(comment.trim());
    } finally {
      handleClose(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      handleClose();
    }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center px-4"
      onClick={handleClose}
    >
      <div
        className={`rounded-[24px] w-full max-w-[608px] h-[530px] relative overflow-hidden transition-all duration-200 ease-out border border-gray-200 shadow-2xl ${
          isAnimating
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-95 translate-y-2"
        } ${isSubmitting ? "bg-status-success" : "bg-white"}`}
        style={isSubmitting ? { backgroundColor: CAMCOM_GREEN } : undefined}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {isSubmitting ? (
          <div className="h-full px-6 py-12 flex flex-col items-center text-center">
            <div className="flex flex-col items-center">
              <div
                className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-white text-xs font-semibold"
                style={{ color: CAMCOM_GREEN }}
              >
                APPROVED
              </div>

              <div
                className="text-white text-2xl font-semibold mt-4"
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 600,
                  letterSpacing: "-0.2px",
                }}
              >
                {numPlate || "N/A"}
              </div>

              <div
                className="text-white mt-3"
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: "20px",
                  lineHeight: "26px",
                  letterSpacing: "-0.01em",
                  textAlign: "center",
                  verticalAlign: "bottom",
                }}
              >
                This case has been approved by {userId || ""}
              </div>
            </div>

            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="text-white/90 text-sm font-medium">
                Submitting…
              </div>

              <div className="flex items-center justify-center gap-2 mt-4">
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className="w-2 h-2 rounded-full bg-white animate-bounce"
                    style={{ animationDelay: `${i * 120}ms` }}
                  />
                ))}
              </div>
            </div>

            <div className="flex items-center justify-center gap-2">
              <img
                src="https://api.builder.io/api/v1/image/assets/TEMP/60d80dc90582a1d0492b0dd3ef3cde4577af1816?width=64"
                alt="CAMCOM"
                className="h-5 w-5"
              />
              <span className="text-black text-xs font-medium uppercase tracking-tight">
                POWERED BY CAMCOM
              </span>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col">
            <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex items-start justify-between gap-4">
              <h2
                className="text-text-primary text-base font-semibold leading-normal"
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 600,
                  letterSpacing: "-0.16px",
                }}
              >
                {title}
              </h2>
              <button
                type="button"
                onClick={() => handleClose(true)}
                disabled={isSubmitting}
                aria-label="Close approve popup"
                className="rounded-[10px] p-2 text-text-secondary transition-colors hover:bg-gray-100 hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5 flex-1 flex flex-col">
              <div
                className="block text-text-secondary text-xs font-medium uppercase tracking-[-0.12px]"
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 500,
                }}
              >
                CASE ID
              </div>
              <div
                className="text-text-primary text-base font-semibold mt-1"
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 600,
                  letterSpacing: "-0.16px",
                }}
              >
                <div className="flex items-center gap-3">
                  <span>{numPlate || "N/A"}</span>
                  <span
                    className="text-white text-xs font-semibold px-3 py-1 rounded-full select-none"
                    style={{ backgroundColor: CAMCOM_GREEN }}
                  >
                    APPROVED
                  </span>
                </div>
              </div>

              <div
                className="block text-text-secondary text-xs font-medium uppercase tracking-[-0.12px] mt-4"
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 500,
                }}
              >
                NOTE
              </div>

              <textarea
                className="w-full flex-1 min-h-[240px] rounded-xl border border-gray-200 px-4 py-3 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-200 mt-4"
                placeholder="Enter comments… (Ctrl/⌘ + Enter to submit)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                autoFocus
              />
            </div>

            <div className="px-6 pb-6 mt-auto">
              <button
                type="button"
                className={
                  canSubmit
                    ? "w-full px-4 py-3 rounded-[999px] bg-white border border-status-success text-sm font-semibold text-status-success hover:bg-gray-50"
                    : "w-full px-4 py-3 rounded-[999px] bg-gray-100 border border-gray-200 text-sm font-semibold text-gray-400 cursor-not-allowed"
                }
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {submitLabel}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
